import type { ComponentProps } from "./article-item.d";

import dayjs from "dayjs";
import React, { useCallback } from "react";

import PhotoGallery from "../shared/components/photo-gallery";
import Layout from "../shared/layouts/default";
import { useAppStore } from "../shared/stores";
import { getCookie, http, setCookie } from "../shared/utils/client";
import "./article-item.scss";
import { useEffectOnce } from "/react4xp/shared/hooks";

function Component(props: ComponentProps) {
	const { item } = props;

	const appStore = useAppStore((state) => state);

	const incrementItemViews = useCallback(() => {
		if (appStore.sitePath == null || appStore.appName == null || item == null) return;

		const storageKey = `viewed_${item._id}`;
		if (getCookie(storageKey)) return;

		setCookie(storageKey, "true", 24);
		http({
			url: "api/article?incrementViewed",
			method: "PUT",
			body: JSON.stringify({ _id: item._id }),
		});
	}, [appStore.sitePath, appStore.appName, item]);

	useEffectOnce(() => {
		incrementItemViews();
	}, [incrementItemViews]);

	const contentWithoutImages = item?.data?.content?.replace(/<figure[^>]*>[\s\S]*?<\/figure>/g, "");

	const links = Array.isArray(item?.data.linkContent)
		? item?.data.linkContent
		: item?.data.linkContent
			? [item?.data.linkContent]
			: [];

	return (
		<Layout {...props}>
			<main className="article-item">
				<div className="container">
					<div className="row my-3">
						<div className="col">
							<h2 className="title">{item?.displayName}</h2>

							<p className="d-flex gap-3 text-muted">
								<span className="d-flex gap-2">
									<i className="fa-solid fa-calendar"></i>
									<small>{dayjs(item?.publish?.from ?? item?.modifiedTime).format("DD.MM.YYYY")}</small>
								</span>

								<span className="d-flex gap-2">
									<i className="fa-solid fa-eye"></i>
									<small>{`${item?.data?.viewed ?? 0}`}</small>
								</span>
							</p>
						</div>
					</div>

					<div className="row my-3">
						<div className="col">
							<div
								dangerouslySetInnerHTML={{
									__html: contentWithoutImages ?? "",
								}}
							></div>
							<PhotoGallery content={item?.data?.content ?? ""} />
						</div>
					</div>

					{item.data.accordionItems && item.data.accordionItems.length > 0 && (
						<div className="accordion" id={`accordion-${item._id}`}>
							{item.data.accordionItems.map((accordionItem, index) => (
								<>
									<div>
										{accordionItem.MainTitle && <h3 className="accordion-main-title">{accordionItem.MainTitle}</h3>}
									</div>
									<div className="accordion-item" key={`accordion-item-${index}`}>
										<h2 className="accordion-header">
											<button
												className={`accordion-button`}
												type="button"
												data-bs-toggle="collapse"
												data-bs-target={`#collapse-${item._id}-${index}`}
												aria-expanded={index === 0 ? "true" : "false"}
												aria-controls={`collapse-${item._id}-${index}`}
											>
												{item.data.showNumbering && <div className="item-number">{index + 1}</div>}
												<div dangerouslySetInnerHTML={{ __html: accordionItem.panelTitle }} />
											</button>
										</h2>
										<div
											id={`collapse-${item._id}-${index}`}
											className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
											data-bs-parent={`#accordion-${item._id}`}
										>
											<div className="accordion-body">
												<div dangerouslySetInnerHTML={{ __html: accordionItem.panelContent }} />
											</div>
										</div>
									</div>
								</>
							))}
							{links.length > 0 && <h5 style={{ margin: 0 }}>{appStore.phrases?.["helpfulLinks"]}</h5>}
							{links.map((link, index) => (
								<a
									key={`article-link-${index}`}
									href={link.linkUrl}
									className="white-card-link"
									target="_blank"
									rel="noopener noreferrer"
								>
									<i className="fa-solid fa-up-right-from-square"></i>
									{link.linkTitle}
								</a>
							))}
						</div>
					)}
				</div>
			</main>
		</Layout>
	);
}

export default function WrappedComponent(props: ComponentProps) {
	return <Component {...props} />;
}
